import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField, InputAdornment, Button, Radio, Switch, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterDropdown from './FilterDropdown';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BotUserProfile from './BotUserProfile'; 
import { setBotFilter } from '../../containers/app-container/reducer/filterSlice';
import { fetchUserData } from '../../api/user';
import { useSelector } from 'react-redux';
import { ListEmpty, ListError, ListLoading } from './ListStatus';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  maxHeight: '80vh', // Restricts height for scroll
  overflowY: 'auto', // Adds vertical scroll
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '20px',
  p: '20px 20px 0 20px',
};

// Common style object for FormControlLabel and Checkbox
const checkboxStyle = {
  display: 'flex',
  // justifyContent: 'space-between',
  width: '100%',
  marginLeft: '0',
  fontSize: '14px', // Label font size
  '.MuiTypography-root': {
    fontSize: '14px', // Label text font size
  },
  control: {
    color: '#916008', // Unchecked color
    '&.Mui-checked': {
      color: '#916008', // Checked color
    },
  },
};

const filters = [
  'All',
  'New Messages',
  'Unread Messages',
  'Recent Initiate Messages'
]

const ChatList = React.forwardRef(({ searchTerm, setSearchTerm, chatData, onChatClick, selectedChat, handleSearch, botUsersLoading, handleScroll,selectedFilter,setSelectedFilter, handleUserOnline, handleUserOffline, error, onRetry }, ref) => {
 const [filterType,setFilterType] = useState(selectedFilter)
   const filter = useSelector((state) => state.filter);
   const debounceTimeoutRef = useRef(null);
  const [pinnedIds, setPinnedIds] = useState(() => new Set());
  // Optimistic isOnLine values from the toggle, keyed by id rather than by list index so
  // they survive the reordering that an incoming message causes.
  const [onlineOverrides, setOnlineOverrides] = useState({});

  // A fresh list from the server supersedes any optimistic toggle, which is what the old
  // effect-based sync did implicitly by overwriting `chats` wholesale. Done during render
  // (React's documented "adjust state when props change") so it resolves before commit
  // and never paints an intermediate frame.
  const prevChatDataRef = useRef(chatData);
  if (prevChatDataRef.current !== chatData) {
    prevChatDataRef.current = chatData;
    if (Object.keys(onlineOverrides).length) setOnlineOverrides({});
  }

  // Derived, not mirrored — see the matching note in ChatListUser. The old state-synced-
  // by-effect version rendered once with the previous list on every prop change; here
  // that shows up when refreshBotListPreservingSelection replaces the bot list after an
  // incoming message, flashing the pre-refresh order.
  const chats = useMemo(() => {
    let list = chatData || [];
    if (Object.keys(onlineOverrides).length) {
      list = list.map((c) =>
        c && c._id in onlineOverrides ? { ...c, isOnLine: onlineOverrides[c._id] } : c
      );
    }
    if (pinnedIds.size) {
      list = [...list].sort(
        (a, b) => (pinnedIds.has(b?._id) ? 1 : 0) - (pinnedIds.has(a?._id) ? 1 : 0)
      );
    }
    return list;
  }, [chatData, pinnedIds, onlineOverrides]);

  // WEB-48: the highlight was the only thing naming the active bot, and this list is
  // paginated (50 at a time) — so the selected bot was usually below the fold and the
  // inbox looked like it belonged to nobody. Pull it back into view.
  //
  // Keyed on the selected id, NOT on `chats`: an incoming message unshifts its bot to
  // the top of the list (updateBot in chat-container), and re-scrolling on every such
  // reorder would yank the list around while someone is reading it.
  const selectedRowRef = useRef(null);
  const lastScrolledIdRef = useRef(null);
  useEffect(() => {
    const id = selectedChat?._id;
    if (!id || lastScrolledIdRef.current === id) return;

    // The row may not be mounted yet when selection changes (list still paging in), and
    // scrollIntoView is a silent no-op on an element with no layout box. Retry across a
    // few frames rather than assuming the first attempt landed.
    let frames = 0;
    let raf;
    const tryScroll = () => {
      const el = selectedRowRef.current;
      if (el?.offsetParent) {
        el.scrollIntoView({ block: 'nearest' });
        lastScrolledIdRef.current = id;
        return;
      }
      if (++frames < 10) raf = requestAnimationFrame(tryScroll);
    };
    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [selectedChat?._id, chats]);

  useEffect(() => {
    const listElement = ref.current;

    // Only add the scroll event listener if the listElement is available
    if (listElement) {
      // console.log('Adding scroll event listener');
      listElement.addEventListener('scroll', handleScroll);
    }

    // Cleanup the event listener when the component unmounts or botUsersLoading changes
    return () => {
      if (listElement) {
        // console.log('Removing scroll event listener');
        listElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [botUsersLoading, chats]);


  // Function to handle pinning/unpinning
  const handlePinToggle = (chat) => {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(chat?._id)) next.delete(chat?._id);
      else next.add(chat?._id);
      return next;
    });
  };

  const handleOnlineToggle = (e, chat, index) => {
    e.stopPropagation();
    const next = !chat?.isOnLine;
    if (next) {
      handleUserOnline?.(chat?._id);
    } else {
      handleUserOffline?.(chat?._id);
    }
    setOnlineOverrides((prev) => ({ ...prev, [chat?._id]: next }));
  };

  const filteredChats = chats;
  // .filter
  // (chat) => chat.userName.toLowerCase().includes(searchTerm.toLowerCase())
  // chat.role.toLowerCase().includes(searchTerm.toLowerCase())

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSearchChange = function (e) {
    setSearchTerm(e.target.value)
    // handleSearch(e.target.value)
  }

   const [openProfile, setOpenProfile] = useState({status:false,userId:null,details:null,loading:false});

  const toggleDrawer = (status, userId = null) => () => {
    setOpenProfile({ status, userId, details: null, loading: status });
  };

  const fetchData = async () => {
    try {
      await fetchUserData(openProfile?.userId, (data) => {setOpenProfile({ ...openProfile, details: data,loading:false }) });
    } catch (error) {
      console.error('Error while loading real-time data', error);
      setOpenProfile({ ...openProfile, details: null,loading:false })
    }
  };

  useEffect(() => {
    if(openProfile?.userId) fetchData();
  }, [openProfile?.userId]);

  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return

    clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      handleSearch(searchTerm)
    }, 300);

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchTerm]);

  return (
    <div className="chatlist">
      <div className="modal-dialog-scrollable">
        {/* Conditionally render based on whether there are filtered chats */}

        <div className="modal-content ">
          <div className="chat-header p-16">
           
            <div className="msg-search w-100">
              <TextField
                type="text"
                id="inlineFormInputGroup"
                placeholder="Search"
                aria-label="search"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"
                      onClick={handleSearch}>
                      <SearchIcon sx={{ width: '18px', height: '18px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '32px',
                    fontSize: '14px',
                    color: '#8FA0AF',
                    '&:hover fieldset': {
                      borderColor: '#FF4A4A',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF4A4A',
                    },
                    '& input': {
                      padding:'5.5px 14px 5.5px 0',  
                    },
                  },
                  '& .MuiInputAdornment-root': {
                    color: 'grey',
                    '&:hover': {
                      color: '#FF4A4A',
                    },
                  },
                }}
              />
            </div>
            <div className="hdtopsetup pt-16">
              <Typography sx={{ color: '#808080', fontSize: "16px", fontWeight: '500' }}>{filter?.statsTitle ?? "Bot User"}</Typography>
              <div className="font-12 fw-medium color7b d-flex justify-content-between align-items-center">
                {botUsersLoading ? "Loading..." : selectedFilter}{' '}
                <Button
                  onClick={handleOpen}
                  sx={{ padding: '5px', minWidth: '40px' }}
                >
                  <img
                    src="/assets/images/messages/filter-icon.svg"
                    className="img-fluid"
                    alt=""
                  />
                </Button>
                {/* <FilterDropdown /> */}
              </div>
            </div>
          </div>
          {/* Order matters: error before empty, and loading before empty, so a failed or
              in-flight request can never render as "No Messages Yet?". */}
          { error ? (
            <ListError onRetry={onRetry} />
          ) : botUsersLoading && filteredChats.length === 0 ? (
            <ListLoading />
          ) : filteredChats.length > 0 ? (
            <div className="modal-body p-0 overflowauto messagesList" ref={ref}>
              <div className="chat-lists">
                <div className="chat-list" ref={ref}>
                  {filteredChats.map((chat, index) => (
                    <div
                      key={chat?._id ?? index}
                      ref={selectedChat?._id === chat?._id ? selectedRowRef : null}
                      className={`d-flex align-items-center flex-row py-16 px-24 conversation gap-10${
                        selectedChat?._id === chat?._id ? ' selected' : ''
                      }`}
                      onClick={() => onChatClick(chat)}
                    >
                      <div className="flex-shrink-0 cs-avatar">
                        <img
                          className="img-fluid"
                          src={chat.profilePicture}
                          alt="user img"
                        />

                        <span className={chat?.isOnLine ? 'active' : 'inactive'} />
                      </div>
                      <div className="flex-grow-1 conversation_content">
                        <h3>{chat.firstName ?? chat.userName}</h3>
                        {/* <p>{chat.role}</p> */}
                      </div>
                      <div className="conversationTimeUnreadPin">
                        {/* <div className="time text-nowrap d-flex align-items-center justify-content-end">
                          10:49 AM
                        </div> */}
                        <div className="conversationPin_unread d-flex align-items-center justify-content-end gap-1">
                          {/* <div
                            className="pin-unpinBg"
                            style={{ height: '22px', overflow: 'hidden' }}
                          >
                            <Button
                              className={`pin-unpin ${chat.pinned ? 'show' : ''
                                }`}
                              sx={{
                                minWidth: '30px',
                                padding: '0',
                                height: '20px',
                                lineHeight: '27px',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePinToggle(index);
                              }}
                            >
                              <img
                                src={
                                  chat.pinned
                                    ? '/assets/images/messages/pinned.svg'
                                    : '/assets/images/messages/pin.svg'
                                }
                                className="img-fluid"
                                alt="pin icon"
                              />
                            </Button>
                          </div> */}
                          {chat.notificationCounter > 0 && (
                            <div className="conversation_unread">
                              {chat.notificationCounter}
                            </div>
                          )}
                          <Tooltip title={chat?.isOnLine ? 'Online — tap to set offline' : 'Offline — tap to set online'}>
                            <Switch
                              size="small"
                              checked={!!chat?.isOnLine}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleOnlineToggle(e, chat, index)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#1F8A5B' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1F8A5B' },
                              }}
                            />
                          </Tooltip>
                          <MoreVertIcon onClick={toggleDrawer(true,chat?._id)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {botUsersLoading && <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      Loading...
                    </Typography>
                  </Box>}
                </div>
               
              </div>
            </div>
          ) : (
            <ListEmpty />
          )}
        </div>
      </div>



      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {/* Filters */}
          {/* <FormGroup>
            {[
              'New Messages',
              'Favorite/Not Favorite',
              'Unread Messages',
              'Read Messages',
              'Starred or Pinned Messages',
              'Message Age',
              'Messages with Attachments',
              'Blocked Users',
              'Verified Profiles',
            ].map((label, index) => (
              <FormControlLabel
                key={index}
                sx={checkboxStyle} // Apply the common styling here
                control={<Checkbox defaultChecked={index === 0} />} // Default checked for the first item
                label={label}
                labelPlacement="start"
              />
            ))}
          </FormGroup> */}

          <FormGroup>
            {filters.map((label, index) => (
              <FormControlLabel
                key={index}
                sx={checkboxStyle}
                control={<Radio
                  onChange={() => {
                    setFilterType(label)
                  }}
                  checked={filterType === label ? true : false}
                   sx={{
            color: "#999", // default color
            "&.Mui-checked": {
              color: "#ff0000", // checked color
            },
          }}
                />}
                label={label}
                // labelPlacement="start"
              />
            ))}
          </FormGroup>

          {/* Buttons */}
          <Box
            className="border-top mt-16 position-sticky bottom-0 z-3 bg-base pb-16"
            sx={{ marginTop: '20px', background: '#fff' }}
          >
            <Button
              onClick={()=>{handleClose();setSelectedFilter('All');setFilterType('')}}
              className="w-100"
              sx={{
                background: '#fff',
                color: '#000',
                fontSize: '16px',
                textTransform: 'capitalize',
                fontFamily: 'Neue Montreal',
                width: '100%',
                marginBottom: '10px',
              }}
            >
              Reset
            </Button>
            <Button
              onClick={()=>{handleClose();setSelectedFilter(filterType || "All")}}
              className="w-100"
              sx={{
                background: '#BE1F1F',
                color: '#fff',
                fontSize: '16px',
                textTransform: 'capitalize',
                fontFamily: 'Neue Montreal',
                width: '100%',
                '&:hover': {
                  background: '#9B1A1A'
                }
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Modal>

<BotUserProfile open={openProfile?.status} toggleDrawer={toggleDrawer} selectedBot={openProfile?.details} loading={openProfile?.loading}/>
    </div>
  );
});

export default ChatList;
